import '../../styles/blog.css';
import '../../styles/tutorials.css';
import { SiteLink as Link } from '../../components/SiteLink';
import { PageShell } from '../../components';
import { postComponents } from '../../components/mdx';
import { CodeBlock } from '../../components/tutorials/CodeBlock';
import { Breadcrumbs } from '../../components/tutorials/Breadcrumbs';
import { TutorialSidebar } from '../../components/tutorials/Sidebar';
import { signupFrom } from '../../components/blog/Figure';
import { formatDate, sectionOfTopic, testedParts, topicLabel } from '../../content';
import type { Tutorial } from '../../content';

const tutorialComponents = { ...postComponents, pre: CodeBlock };

/**
 * One tutorial: the sidebar on the left, the article on the right. The body
 * uses the blog's reading styles (.bl-body) so prose, links and inline code
 * match the posts; code blocks, the meta line and the layout are tutorials.css.
 */
export default function TutorialPage({ tutorial }: { tutorial: Tutorial }) {
  const { Content } = tutorial;
  const updated = tutorial.updated ?? tutorial.date;
  const topic = topicLabel(tutorial.topic);
  const section = sectionOfTopic(tutorial.topic);
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: tutorial.title,
    description: tutorial.description,
    datePublished: tutorial.date,
    dateModified: updated,
    author: { '@type': 'Person', name: tutorial.author, url: 'https://logicalbytes.dev/about/' },
    publisher: { '@type': 'Organization', name: 'Statio', url: 'https://statio.dev/' },
    mainEntityOfPage: `https://statio.dev/tutorials/${tutorial.slug}/`,
  };

  return (
    <PageShell>
      <div className="s-wrap tu">
        <TutorialSidebar current={tutorial} />
        <article className="tu-main">
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
          {section && <Breadcrumbs section={section} tutorial={tutorial} />}
          <header className="tu-head">
            {topic && <p className="tu-topic">{topic}</p>}
            <h1>{tutorial.title}</h1>
            {tutorial.lede && <p className="tu-lede">{tutorial.lede}</p>}
            <dl className="tu-meta">
              <div>
                <dt>By</dt>
                <dd>{tutorial.author}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatDate(updated)}</dd>
              </div>
              <div>
                <dt>{testedParts(tutorial.tested).label}</dt>
                <dd>{testedParts(tutorial.tested).value}</dd>
              </div>
              <div>
                <dt>Reading time</dt>
                <dd>{tutorial.readMinutes} min</dd>
              </div>
            </dl>
            {tutorial.topic !== 'basics' && (
              <p className="tu-new">
                New to MCP?{' '}
                <Link className="s-link" to="/tutorials/what-is-mcp">
                  What is an MCP server?
                </Link>{' '}
                covers the basics first.
              </p>
            )}
          </header>
          <div className="bl-body tu-body">
            <Content components={tutorialComponents} />
          </div>
          <aside className="bl-cta tu-cta">
            <p>
              <b>Run these servers through Statio.</b> Grant each agent only the tools it needs, keep the keys off
              laptops, and see every call. Free for 3 servers.
            </p>
            <a
              className="s-btn s-btn-pri"
              href={signupFrom(`tutorial-${tutorial.slug}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Start free
            </a>
          </aside>
        </article>
      </div>
    </PageShell>
  );
}
